import { apiClient } from '@/shared/api/apiClient';
import {
  handleRequest,
  type RequestHandlerOptions,
} from '@/shared/api/apiWrapper';
import type {
  LoginMfaRequestDto,
  LoginMfaRequiredResponseDto,
  SendOtpCodeRequestDto,
  SendOtpCodeSuccessResponseDto,
  SendPasswordRecoveryCodeRequestDto,
  SetNewPasswordRequestDto,
  SetNewPasswordResponseDto,
  VerifyPasswordRecoveryCodeRequestDto,
} from './types';
import type { Session } from '@/entities/session/model/types';
import { normalizeIdentityTypesInResponse } from '@/entities/mfa-identity/lib/normalizeIdentityType';
import { MFA_REQUIRED_RESPONSE_CODE } from '@/shared/config';

export const login = async <T>(
  args: T | LoginMfaRequestDto,
  options?: RequestHandlerOptions
): Promise<Session | LoginMfaRequiredResponseDto> => {
  const request = () => apiClient.post('/token/login', args);

  const response = await handleRequest<Session | LoginMfaRequiredResponseDto>(
    request,
    options
  );

  // Normalize identityTypes when receiving MFA required response
  // Note: API returns objects with name/isEnabled, but type says MFAIdentityType[]
  if (
    response.data &&
    typeof response.data === 'object' &&
    'code' in response.data &&
    response.data.code === MFA_REQUIRED_RESPONSE_CODE
  ) {
    return normalizeIdentityTypesInResponse(
      response.data as unknown as Record<string, unknown>
    ) as unknown as LoginMfaRequiredResponseDto;
  }

  return response.data;
};

export const sendOtpCode = async (args: SendOtpCodeRequestDto) => {
  const request = () => apiClient.post('/token/login/twofa/otp/send', args);
  const response = await handleRequest<SendOtpCodeSuccessResponseDto>(request);
  return response.data;
};

export const sendPasswordRecoveryCode = async (
  args: SendPasswordRecoveryCodeRequestDto
) => {
  const response = await handleRequest<string>(() =>
    apiClient.post('/security/password/recovery/request', args)
  );

  return { success: response.data === 'Success' };
};

export const verifyPasswordRecoveryCode = async (
  args: VerifyPasswordRecoveryCodeRequestDto
) => {
  const response = await handleRequest<{ code?: string; message?: string }>(
    () => apiClient.post('/security/password/recovery/change', args),
    { throwOnError: false }
  );

  return response;
};

export const setNewPassword = async (args: SetNewPasswordRequestDto) => {
  const response = await handleRequest<SetNewPasswordResponseDto>(() =>
    apiClient.post('/security/password/recovery/change', args)
  );
  return response.data;
};
